"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Déplie les accents pour une comparaison insensible à la casse et aux
// accents : "generateur" doit trouver "générateur".
const normalize = (value) =>
    value
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();

const MAX_RESULTS = 8;

/**
 * Filtre instantané sur les termes d'UN glossaire, entièrement côté client.
 *
 * Remplace SpeedSearch sur cette page : SpeedSearch n'interroge que les index
 * Algolia des formations et des articles, jamais les glossaires, donc aucun
 * terme n'y apparaît jamais. Ici, tous les termes sont déjà présents dans le
 * HTML rendu par le serveur (c'est ce qui alimente la liste par lettre) :
 * un filtre côté client ne coûte ni appel réseau ni index à tenir à jour.
 */
export default function GlossaryTermSearch({ terms, glossarySlug }) {
    const t = useTranslations();
    const router = useRouter();
    const [query, setQuery] = useState("");
    const [highlighted, setHighlighted] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const results = useMemo(() => {
        const q = normalize(query.trim());
        if (!q) return [];
        return terms
            .filter((term) => normalize(term.name).includes(q))
            .slice(0, MAX_RESULTS);
    }, [query, terms]);

    const open = query.trim().length > 0;

    // Ferme sur un clic extérieur plutôt que sur le blur de l'input : le blur
    // se déclenche avant le clic sur un résultat et fermerait la liste avant
    // que la navigation n'ait lieu.
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setQuery("");
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    useEffect(() => {
        setHighlighted(0);
    }, [query]);

    const onKeyDown = (e) => {
        if (!open || results.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((i) => (i + 1) % results.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((i) => (i - 1 + results.length) % results.length);
        } else if (e.key === "Escape") {
            setQuery("");
            inputRef.current?.blur();
        } else if (e.key === "Enter") {
            e.preventDefault();
            const target = results[highlighted];
            if (target) {
                router.push({
                    pathname: "/glossaries/[slug]/[termSlug]",
                    params: { slug: glossarySlug, termSlug: target.slug },
                });
            }
        }
    };

    return (
        <div ref={containerRef} className="relative w-full md:w-80">
            <div className="relative">
                <SolarIcon
                    name="magnifer-linear"
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-grayDark-500"
                    style={{ width: 17, height: 17 }}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t("Glossary_search_placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-9 text-sm text-gray-900 transition-colors duration-150 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-primary/20 dark:border-borderDark/60 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-grayDark-500 dark:focus:bg-bgDark-800"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            inputRef.current?.focus();
                        }}
                        aria-label={t("Glossary_search_clear")}
                        className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-grayDark-300"
                    >
                        <SolarIcon name="close-circle-linear" style={{ width: 16, height: 16 }} />
                    </button>
                )}
            </div>

            {open && (
                <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl shadow-black/[0.06] dark:border-borderDark/60 dark:bg-bgDark-800 dark:shadow-black/30">
                    {results.length > 0 ? (
                        results.map((term, i) => (
                            <Link
                                key={term.slug}
                                href={{
                                    pathname: "/glossaries/[slug]/[termSlug]",
                                    params: { slug: glossarySlug, termSlug: term.slug },
                                }}
                                onMouseEnter={() => setHighlighted(i)}
                                className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-100 ${
                                    highlighted === i
                                        ? "bg-primary/[0.08] text-primary dark:bg-primary/10"
                                        : "text-gray-700 dark:text-grayDark-200"
                                }`}
                            >
                                <span className="font-medium leading-snug">{term.name}</span>
                                <SolarIcon
                                    name="arrow-right-linear"
                                    className={`mt-0.5 shrink-0 transition-transform duration-150 ${
                                        highlighted === i ? "translate-x-0.5" : ""
                                    }`}
                                    style={{ width: 14, height: 14 }}
                                />
                            </Link>
                        ))
                    ) : (
                        <div className="px-3 py-4 text-center text-sm text-gray-400 dark:text-grayDark-500">
                            {t("Glossary_search_empty", { query: query.trim() })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
